import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Grid, useMediaQuery, useTheme } from '@mui/material'

import { ChangesTable } from '../changes-table'
import { PaginationButtons, PaginationContext } from 'src/components/pagination-buttons'
import { FoundItemsCounter } from 'src/components/found-items-counter'
import { Filter } from 'src/components/filter'
import { NavigationButtons } from '../navigation-buttons'
import {
  initialChangesFilterValues,
  useAccountsSelectionOptions,
  useChanges,
  useFilter,
  usePagination,
  useUsersSelectionOptions,
} from 'src/hooks'

import { countOffset, sortToObject } from 'src/common/utils'
import {
  changeRelationFormSelectionOptions,
  changesSortOptions,
  changeTypesSelectionOptions,
  defaultLimit,
  defaultPage,
  defaultSort,
} from './utils'

const ChangesLogList = () => {
  const [t] = useTranslation()
  const [filter, setFilter] = useFilter({ initialState: initialChangesFilterValues })
  const [sort, setSort] = useState(defaultSort)
  const { page, rowsPerPage, setPage, setRowsPerPage } = usePagination(defaultPage, defaultLimit)

  const { data: changes, refetch: refetchChanges } = useChanges({
    ...filter,
    offset: countOffset(page, rowsPerPage),
    limit: rowsPerPage,
    ...sortToObject(sort),
  })

  const usersSelectionOptions = useUsersSelectionOptions()
  const accountsSelectionOptions = useAccountsSelectionOptions()

  const theme = useTheme()
  const isTablet = useMediaQuery(theme.breakpoints.down('tablet'))

  useEffect(() => {
    refetchChanges()
  }, [filter, page, refetchChanges, rowsPerPage, sort])

  useEffect(() => {
    setPage(defaultPage)
  }, [filter, setPage, sort])

  const paginationValues = {
    count: changes?.count ?? 0,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    isShown: !!changes && changes.count > defaultLimit,
  }

  const items = [
    { ...usersSelectionOptions, withSearch: true },
    { ...accountsSelectionOptions, withSearch: true },
    { ...changeTypesSelectionOptions, withSearch: true },
    { ...changeRelationFormSelectionOptions, withSearch: true },
  ]

  return (
    <Grid container>
      <PaginationContext.Provider value={paginationValues}>
        <Filter
          sort={sort}
          setSort={setSort}
          searchFieldText={t('change-logs-search-input.v')}
          filter={filter}
          setFilter={setFilter}
          selectionOptions={items}
          sortOptions={changesSortOptions}
          initialState={initialChangesFilterValues}
        />
        <NavigationButtons />

        <FoundItemsCounter count={changes?.count} />

        <ChangesTable changes={changes?.data} sort={sort} setSort={setSort} />

        {!isTablet && <PaginationButtons />}
      </PaginationContext.Provider>
    </Grid>
  )
}

export { ChangesLogList }
